<template>
  <nv-tab-page>
    <template slot="toolbar">
      <v-btn color="secondary" class="mb-5" @click="addConfig">
        <v-icon left>mdi-plus</v-icon>
        Add Configuration
      </v-btn>
    </template>
    <v-row>
      <v-col v-for="(config, index) in selectedConfigs" :key="index" cols="12">
        <v-card outlined>
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" sm="4">
                <v-select
                  v-model="config.name"
                  :items="getAvailableConfigsForRow(index)"
                  label="Configuration Name"
                  @change="onConfigChange(index)"
                />
              </v-col>
              <v-col cols="12" sm="7">
                <component
                  :is="getComponentType(config.name)"
                  v-if="config.name"
                  v-model="config.value"
                  v-bind="getComponentProps(config.name)"
                />
              </v-col>
              <v-col cols="12" sm="1">
                <v-btn icon color="error" @click="removeConfig(index)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </nv-tab-page>
</template>

<script>
import HTTP from '@/api/dog';
import { enumFetch } from '@nielsen-media/ui-toolkit-vue-api';

export default {
  name: 'ConfigurationFlagsBulkUpdate',
  props: {
    isEditable: { type: Boolean, default: true },
  },
  data: () => ({
    selectedConfigs: [],
    enumDarReports: [],
    enumSyndicated: [],
    enumSyncProviders: [],
    enumSIVTViewabilities: [],
    enumSIVTProducts: [],
    enumSFCodes: ['cert', 'eu_uat'],
    userOptOut: ['1', 'true', 'yes'],
    configDefinitions: {
      dar_report: { type: 'select', label: 'DAR Report', items: 'enumDarReports' },
      syndicated: { type: 'select', label: 'Syndicated', items: 'enumSyndicated' },
      user_opt_out: { type: 'select', label: 'DAR Optout Value', items: 'userOptOut' },
      sync_providers: {
        type: 'select',
        label: 'Sync Providers',
        items: 'enumSyncProviders',
        multiple: true,
      },
      debug_sf_code: { type: 'select', label: 'Debug SF Code', items: 'enumSFCodes' },
      first_party_id_ttl: { type: 'number', label: 'First Party ID TTL' },
      sivt_viewabilities: {
        type: 'select',
        label: 'Viewability',
        items: 'enumSIVTViewabilities',
        multiple: true,
      },
      sivt_products: {
        type: 'select',
        label: 'SIVT Products',
        items: 'enumSIVTProducts',
        multiple: true,
      },
      sdk_user_optout: { type: 'switch', label: 'SDK User Optout' },
      cookieless_domain_enabled: { type: 'switch', label: 'Browser Cookieless Domain' },
      first_party_id_enabled: { type: 'switch', label: 'First Party ID' },
      hash_vendor_id_enabled: { type: 'switch', label: 'Hash Vendor ID' },
      vendor_id_enabled: { type: 'switch', label: 'Vendor ID' },
      emm_enabled: { type: 'switch', label: 'EMM' },
      sivt_enabled: { type: 'switch', label: 'SIVT' },
      hashed_email_enabled: { type: 'switch', label: 'Hashed Email' },
      static_duration_enabled: { type: 'switch', label: 'Static Duration' },
      sdk_diagnostic_data_enabled: { type: 'switch', label: 'SDK Diagnostic Data' },
      uid2_enabled: { type: 'switch', label: 'UID2' },
      cms: { type: 'switch', label: 'CMS' },
      ad_measurement: { type: 'switch', label: 'Ad Measurement' },
      va_beacon: { type: 'switch', label: 'VA Beacon' },
      multi_lingual_cms: { type: 'switch', label: 'Multi Lingual CMS' },
      video_census: { type: 'switch', label: 'Video Census' },
      legacy_ping: { type: 'switch', label: 'Legacy Ping' },
      agf_identity: { type: 'switch', label: 'AGF Identity' },
      vri: { type: 'switch', label: 'VRI' },
      metadata_ping_enabled: { type: 'switch', label: 'Metadata Ping' },
      nol_debug_session_tracking: { type: 'switch', label: 'Nol Debug Session Tracking' },
      nol_debug_server_url: { type: 'string', label: 'Nol Debug Server URL' },
    },
  }),

  created() {
    this.enumFetch(HTTP, {
      enumDarReports: '/applications/enum/dar_reports',
      enumSyndicated: '/applications/enum/syndicated',
      enumSyncProviders: '/applications/enum/sync_providers',
      enumSIVTViewabilities: '/applications/enum/viewabilities',
      enumSIVTProducts: '/applications/enum/sivt',
    });
  },
  methods: {
    getAvailableConfigsForRow(rowIndex) {
      const selectedNames = this.selectedConfigs
        .map((c, idx) => (idx !== rowIndex ? c.name : null))
        .filter(n => n !== null);

      return Object.keys(this.configDefinitions)
        .filter(key => !selectedNames.includes(key))
        .map(key => ({
          text: this.configDefinitions[key].label,
          value: key,
        }));
    },
    addConfig() {
      this.selectedConfigs.unshift({ name: null, value: null });
    },
    removeConfig(index) {
      this.selectedConfigs.splice(index, 1);
    },
    onConfigChange(index) {
      const config = this.selectedConfigs[index];
      const def = this.configDefinitions[config.name];
      config.value = def.type === 'switch' ? false : null;
    },
    getComponentType(configName) {
      if (!configName) return null;
      const def = this.configDefinitions[configName];
      if (def.type === 'select') return 'v-select';
      if (def.type === 'number') return 'v-text-field';
      if (def.type === 'switch') return 'v-switch';
      return 'v-text-field';
    },
    getComponentProps(configName) {
      if (!configName) return {};
      const def = this.configDefinitions[configName];
      const props = { label: def.label };

      if (def.type === 'select') {
        props.items = this[def.items];
        if (def.multiple) {
          props.multiple = true;
          props.chips = true;
          props['deletable-chips'] = true;
        }
        if (
          def.items === 'enumSyncProviders' ||
          def.items === 'enumSIVTViewabilities' ||
          def.items === 'enumSIVTProducts'
        ) {
          props['item-text'] = 'name';
          props['item-value'] = 'code';
        }
      } else if (def.type === 'number') {
        props.type = 'number';
      }

      if (configName === 'nol_debug_server_url') {
        props.disabled = !this.handleDebugServerUrl();
      }

      return props;
    },
    getData() {
      const data = {};
      this.selectedConfigs.forEach(config => {
        if (config.name && config.value !== null && config.value !== '') {
          data[config.name] = config.value;
        }
      });
      return data;
    },
    handleDebugServerUrl() {
      const debugSessionConfig = this.selectedConfigs.find(
        c => c.name === 'nol_debug_session_tracking',
      );
      const isEnabled = debugSessionConfig && debugSessionConfig.value === true;
      if (!isEnabled) {
        const serverUrlConfig = this.selectedConfigs.find(c => c.name === 'nol_debug_server_url');
        if (serverUrlConfig) serverUrlConfig.value = '';
      }
      return isEnabled;
    },
    enumFetch,
  },
};
</script>
